import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { Plane, Clock, Shield, Globe, Package, Zap, CheckCircle } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useState } from "react";
import airFreightImg from "@/assets/air-freight.jpg";
import bannerTerminal from "@/assets/banner-terminal.jpg";

const AirFreight = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const services = [
    {
      icon: Zap,
      title: "Express Air Cargo",
      description: "Time-critical shipments delivered within 24-72 hours worldwide",
      features: ["Next-day delivery options", "Priority handling", "Real-time tracking", "Dedicated customer support"]
    },
    {
      icon: Package,
      title: "Standard Air Freight",
      description: "Cost-effective air cargo solutions for regular shipments",
      features: ["Consolidated shipments", "Scheduled departures", "Full documentation", "Customs clearance included"]
    },
    {
      icon: Shield,
      title: "Dangerous Goods",
      description: "Certified handling of hazardous materials by air",
      features: ["IATA certified", "Proper packaging", "Documentation compliance", "Safety protocols"]
    },
    {
      icon: Plane,
      title: "Charter Services",
      description: "Dedicated aircraft for oversized or urgent cargo",
      features: ["Full aircraft charter", "Part charter options", "Flexible scheduling", "Any cargo type"]
    }
  ];

  const routes = [
    { from: "Athens (ATH)", to: "New York (JFK)", time: "10-12 hours", frequency: "Daily" },
    { from: "Athens (ATH)", to: "Shanghai (PVG)", time: "12-14 hours", frequency: "5x weekly" },
    { from: "Athens (ATH)", to: "Dubai (DXB)", time: "4-5 hours", frequency: "Daily" },
    { from: "Athens (ATH)", to: "London (LHR)", time: "3-4 hours", frequency: "Daily" },
    { from: "Athens (ATH)", to: "Singapore (SIN)", time: "11-13 hours", frequency: "4x weekly" },
    { from: "Athens (ATH)", to: "Frankfurt (FRA)", time: "2-3 hours", frequency: "Daily" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src={airFreightImg} 
          alt="Air Freight Services" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70" />
        <div className="relative z-10 text-center text-primary-foreground px-4 animate-fade-in">
          <Plane className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Air Freight Services</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8 opacity-90">
            Fast, reliable air cargo solutions connecting Athens to every major hub worldwide. From express deliveries to charter services.
          </p>
          <Button size="lg" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
            Get Air Freight Quote
          </Button>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Speed Meets Reliability</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  When time is critical, our air freight services deliver. With partnerships across major airlines and a network spanning 150+ airports, we ensure your cargo reaches its destination on time, every time.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  From Athens International Airport, we offer direct connections to all major global hubs, with consolidation services that maximize efficiency while minimizing costs.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">150+</div>
                    <div className="text-sm text-muted-foreground">Airport Connections</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">24/7</div>
                    <div className="text-sm text-muted-foreground">Operations Center</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">99.2%</div>
                    <div className="text-sm text-muted-foreground">On-Time Delivery</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">48hr</div>
                    <div className="text-sm text-muted-foreground">Average Transit Time</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={bannerTerminal} 
                  alt="Air Cargo Terminal" 
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </section>

          {/* Service Types */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Air Freight Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-muted-foreground mb-6">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 text-primary mr-2" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Process */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Our Air Freight Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                { step: "1", title: "Booking", desc: "Submit your shipment details" },
                { step: "2", title: "Pickup", desc: "We collect from your location" },
                { step: "3", title: "Processing", desc: "Customs & documentation" },
                { step: "4", title: "Flight", desc: "Cargo takes to the skies" },
                { step: "5", title: "Delivery", desc: "Door-to-door completion" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Routes */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Popular Air Routes from Athens</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {routes.map((route, index) => (
                <Card key={index} className="hover:shadow-lg transition-all hover-scale animate-fade-in">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Globe className="h-8 w-8 text-primary" />
                      <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">{route.frequency}</span>
                    </div>
                    <div className="text-lg font-semibold mb-2">{route.from}</div>
                    <div className="flex items-center text-muted-foreground mb-2">
                      <Plane className="h-4 w-4 mr-2" />
                      <span>{route.time}</span>
                    </div>
                    <div className="text-lg font-semibold">{route.to}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Air Freight?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: Clock, title: "Speed", desc: "Express options available with delivery in as little as 24 hours to major destinations" },
                { icon: Shield, title: "Security", desc: "TSA-compliant screening, secure handling facilities, and comprehensive insurance options" },
                { icon: Globe, title: "Global Reach", desc: "Access to 150+ airports through our network of carrier partnerships" }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="text-center hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-primary text-primary-foreground rounded-xl p-12 animate-fade-in">
            <h2 className="text-3xl font-bold mb-4">Ready to Ship by Air?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Get a competitive quote for your air freight needs. Our team in Athens is ready to assist you 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Request Air Freight Quote
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Air Cargo Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="air-freight" />
    </div>
  );
};

export default AirFreight;
